import React from "react";
import Layout from "../../components/Layout";

export default function FSCCertification() {
  return (
 <Layout
      title={"MBA Solutions - FSC Certification"}
      keywords={
        "FSC Certification, FSC CoC, Forest Stewardship Council, Sustainable Packaging, Responsible Printing, MBA Solutions"
      }
    >
      <div className="min-h-screen bg-gray-50 text-gray-800 overflow-hidden pt-20">
        <div className="sectionlayout">
          {/* Hero */}
          <section className="py-16 grid gap-10 md:grid-cols-2 items-center">
            <div data-aos="fade-right">
              <h2 className="text-4xl sm:text-5xl font-extrabold leading-tight">
                FSC Certification — Traceable, Responsible, Trusted
              </h2>
              <p className="mt-6 text-lg text-gray-600">
                Elevate your brand and open doors to premium markets with FSC
                Chain of Custody (CoC), Forest Management (FM), and Controlled
                Wood (CW) certifications. We guide you from gap analysis to
                successful certification and long-term compliance.
              </p>

              <div className="mt-8 flex flex-wrap gap-4">
              
                <a
                  href="#services"
                  className="inline-flex bg-orange-600 items-center gap-2 px-6 py-3 text-white border rounded-lg"
                >
                  Our Services
                </a>
              </div>

              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="p-4 bg-white rounded-lg shadow-sm">
                  <h4 className="font-semibold">5-Year Validity</h4>
                  <p className="text-sm text-gray-500">
                    FSC-CoC certificates are valid for 5 years with required annual audits.
                  </p>
                </div>
                <div className="p-4 bg-white rounded-lg shadow-sm">
                  <h4 className="font-semibold">Gold Standard</h4>
                  <p className="text-sm text-gray-500">
                    Recognized by the Green Building Council's LEED framework.
                  </p>
                </div>
              </div>
            </div>

            <div className="relative" data-aos="fade-left">
              <div className="rounded-2xl overflow-hidden shadow-xl ring-1 ring-black/5">
                <img
                  src="/assets/Sustainable-Forest-Management.webp"
                  alt="sustainable forestry"
                  className="w-full h-80 object-cover"
                />
              </div>

              <div className="absolute -bottom-8 left-6 right-6 mx-auto bg-white p-4 rounded-xl shadow-md flex items-center gap-4">
                <div className="w-16 h-16 rounded-lg bg-orange-50 flex items-center justify-center ring-1 ring-orange-100">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-8 w-8 text-orange-500"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M12 2C8 6 4 9 4 13c0 4 3 6 8 9 5-3 8-5 8-9 0-4-4-7-8-11z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-500">
                    Trusted by manufacturers, printers, traders, and exporters.
                  </p>
                  <p className="font-semibold">FSC CoC • FM • CW</p>
                </div>
              </div>
            </div>
          </section>

          {/* What is FSC? */}
          <section id="about-fsc" className="py-12" data-aos="fade-up">
            <h3 className="text-3xl font-bold text-center">What is FSC Certification?</h3>
            <p className="mt-4  mx-auto text-center text-gray-600">
                The Forest Stewardship Council (FSC) provides the gold standard in forest certification. It guarantees that products come from responsibly managed forests that provide environmental, social, and economic benefits. The FSC label ensures complete traceability from the forest to the end-user, giving consumers confidence in their purchasing decisions.
            </p>
          </section>

          {/* Quick Benefits */}
          <section id="benefits" className="py-12" data-aos="fade-up">
            <h3 className="text-3xl font-bold text-center">Why Pursue FSC Certification?</h3>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  title: "Enhanced Brand Reputation",
                  desc: "Demonstrate your commitment to sustainability and attract environmentally conscious consumers.",
                },
                {
                  title: "Increased Market Access",
                  desc: "Gain entry to global markets and government tenders that mandate certified sustainable products.",
                },
                {
                  title: "Improved Risk Management",
                  desc: "Minimize environmental and social risks associated with your supply chain and sourcing.",
                },
                {
                  title: "Boosted Employee Morale",
                  desc: "Foster a sense of pride and purpose among your staff by committing to sustainable practices.",
                },
              ].map((benefit, i) => (
                <div
                  key={i}
                  data-aos="zoom-in"
                  data-aos-delay={i * 100}
                  className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 ease-in-out"
                >
                  <h4 className="font-semibold">{benefit.title}</h4>
                  <p className="mt-2 text-sm text-gray-500">{benefit.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Services / Who Can Apply */}
          <section id="services" className="py-12" data-aos="fade-up">
            <div className="grid md:grid-cols-2 gap-10 items-start">
              <div>
                <h3 className="text-2xl font-bold">Our FSC Consultancy Services</h3>
                <ul className="mt-6 space-y-4">
                  {[
                    {
                      letter: "A",
                      title: "Gap Analysis",
                      desc: "Assess your current systems against FSC requirements and create a clear improvement plan.",
                    },
                    {
                      letter: "B",
                      title: "System Development & Implementation",
                      desc: "We help you build tailor-made management systems and operational procedures for full compliance.",
                    },
                    {
                      letter: "C",
                      title: "Training & Capacity Building",
                      desc: "Empower your managers, procurement teams, and operational staff with hands-on training.",
                    },
                    {
                      letter: "D",
                      title: "Pre-assessment & Audit Support",
                      desc: "Conduct mock audits, check documentation, and provide full support during the official certification audit.",
                    },
                    {
                        letter: "E",
                        title: "Post-Certification Support",
                        desc: "Assist with annual surveillance audits and continuous improvement to ensure you maintain certification.",
                    },
                  ].map((item) => (
                    <li key={item.letter} className="flex gap-4 items-start">
                      <div className="w-10 h-10 rounded-lg bg-orange-50 flex-shrink-0 flex items-center justify-center text-orange-500 font-semibold">
                        {item.letter}
                      </div>
                      <div>
                        <div className="font-semibold">{item.title}</div>
                        <div className="text-sm text-gray-500">{item.desc}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-2xl font-bold">Who Should Apply?</h3>
                <p className="mt-4 text-gray-600">
                  FSC certification is vital for any company involved in the timber and forest products industry, especially those facing stakeholder pressure for sustainability, seeking access to premium markets, or having a direct impact on forests.
                </p>
                <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-gray-600">
                  <li>• Forest management companies</li>
                  <li>• Logging firms & timber traders</li>
                  <li>• Wood processors, plywood & veneer mills</li>
                  <li>• Printers, publishers & packaging converters</li>
                  <li>• Furniture & handicraft manufacturers</li>
                  <li>• Charcoal briquette producers</li>
                  <li>• Pulp & paper mills</li>
                  <li>• Non-timber forest product suppliers</li>
                </ul>

                <div className="mt-6 bg-white p-4 rounded-lg shadow-sm">
                  <div className="font-semibold">
                    Want a quick eligibility check?
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Contact us for a rapid gap scan and tailored advice.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Types of Certification */}
          <section className="py-12" data-aos="fade-up">
              <h3 className="text-2xl font-bold">Types of FSC Certification</h3>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-white p-6 rounded-xl shadow-md">
                      <h4 className="font-semibold">Forest Management (FM)</h4>
                      <p className="mt-2 text-sm text-gray-500">Certifies that a specific forest area is being managed in line with FSC's rigorous environmental and social standards.</p>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-md">
                      <h4 className="font-semibold">Chain of Custody (CoC)</h4>
                      <p className="mt-2 text-sm text-gray-500">For companies that process or trade forest products, this certifies a traceable supply chain from forest to consumer.</p>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-md">
                      <h4 className="font-semibold">Controlled Wood (CW)</h4>
                      <p className="mt-2 text-sm text-gray-500">Allows manufacturers to mix FSC-certified material with non-certified material under strict controls that mitigate risk.</p>
                  </div>
              </div>
          </section>

          {/* Process Steps */}
          <section id="process" className="py-12">
            <h3 className="text-3xl font-bold text-center">
              Our Streamlined Certification Process
            </h3>
            <div className="mt-8 grid md:grid-cols-4 gap-6">
              {[
                {
                  title: "Contact & Assessment",
                  desc: "Initial consultation to understand your needs, followed by a thorough gap analysis.",
                },
                {
                  title: "Action Plan & System Dev",
                  desc: "We create a detailed roadmap and help develop your FSC management system.",
                },
                {
                  title: "Implementation & Training",
                  desc: "We guide you in implementing procedures, training staff, and conducting internal audits.",
                },
                {
                  title: "Certification Audit",
                  desc: "After a pre-assessment, we support you through the final audit to achieve certification.",
                },
              ].map((s, i) => (
                <div
                  key={i}
                  className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all ease-in-out duration-200 text-center"
                >
                  <div className="w-12 h-12 rounded-full bg-orange-50 mx-auto flex items-center justify-center font-semibold text-orange-500">
                    {i + 1}
                  </div>
                  <h4 className="mt-4 font-semibold">{s.title}</h4>
                  <p className="mt-2 text-sm text-gray-500">{s.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Certificates Showcase */}
          <section id="certificates" className="py-12" data-aos="fade-up">
            <h3 className="text-2xl font-bold">Related Certifications</h3>
            <p className="text-sm text-gray-500 mt-2">
              We also provide consultancy for other global standards to demonstrate your commitment to quality, safety, and sustainability.
            </p>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {[
                {
                  name: "ISO 9001:2015 QMS",
                  desc: "Quality Management System",
                  img: "/assets/certifications/iso9001.png",
                },
                {
                  name: "ISO 14001:2015 EMS",
                  desc: "Environmental Management System",
                  img: "/assets/certifications/iso14001.jfif",
                },
                {
                  name: "ISO 45001:2018 OH&SMS",
                  desc: "Occupational Health & Safety",
                  img: "/assets/certifications/iso45001.png",
                },
                {
                  name: "ISO 22000:2018 FSMS",
                  desc: "Food Safety Management System",
                  img: "/assets/certifications/iso2200.png",
                },
                {
                  name: "FSSC 22000",
                  desc: "Food Safety System Certification",
                  img: "/assets/certifications/FSSC 22000.png",
                },
                {
                  name: "HACCP",
                  desc: "Hazard Analysis & Critical Control Points",
                  img: "/assets/certifications/haccp.jfif",
                },
                {
                  name: "US-FDA Registration",
                  desc: "Food Facility Registration",
                  img: "/assets/certifications/fda.jfif",
                },
                {
                  name: "HALAL Certification",
                  desc: "Halal Certified for Production",
                  img: "/assets/certifications/halal.jpg",
                },
              ].map((cert, i) => (
                <div
                  key={i}
                  className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-transform hover:-translate-y-1 duration-300"
                  data-aos="zoom-in"
                  data-aos-delay={i * 100}
                >
                  <img
                    src={cert.img}
                    alt={cert.name}
                    className="w-full h-44 object-contain"
                  />
                  <div className="p-4">
                    <div className="font-semibold">{cert.name}</div>
                    <div className="text-sm text-gray-500">{cert.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
}
